<script>
import Card from '../../../Components/Card.vue';
import TextInput from '../../../Components/Inputs/TextInput.vue';
import Loading from '../../../Components/Loading.vue';
import Alert from '../../../Components/Alert.vue';
import Label from '../../../Components/Inputs/Label.vue';
import PhoneNumber from '../../../Components/Inputs/PhoneNumber.vue';
import TableOption from '../../../Components/Tables/TableOption.vue';
import { useForm } from '@inertiajs/vue3';
import { RADIO } from '../../../Constants/InputTypes.js';
import AddressAutoComplete from '../../../Components/Inputs/AddressAutoComplete.vue';
import { APPOINTMENT_STATUS } from '../../../Constants/Statuses.js';
import { formatDate } from '../../../Modules/Dates.js';
import Spinner from '../../../Components/Icons/Spinner.vue';
import Button from '../../../Components/Button.vue';
import Lock from '../../../Components/Icons/Lock.vue';
import SlideOut from '../../../Components/SlideOut.vue';
import { closeModal, openModal } from '../../../Modules/Modals.js';
import { isValidEmail } from '../../../Modules/Email.js';
import CommunicationPreferences from './CommunicationPreferences.vue';

export default {
  components: {
    CommunicationPreferences,
    SlideOut,
    Lock,
    Button,
    Spinner,
    AddressAutoComplete,
    TableOption,
    PhoneNumber,
    Label,
    Alert,
    Loading,
    TextInput,
    Card,
  },

  props: {
    masterConfig: Object,
    kioskKey: String,
    kioskSlotInterval: Object,
    customer: Number,
    customerID: Number,
    newCustomer: Object,
    newPreferences: Object,
    autocompleteKey: String,
    searchUpcoming: { type: Boolean, default: false },
    waiterAvailable: {
      type: Boolean,
      default: true,
    },
    tireConnectCustomer: {
      type: Object,
      default: null,
    },
  },

  emits: ['ready', 'update', 'checkin'],

  data() {
    return {
      searchedFields: new Set(),
      searchingForExisting: false,
      foundExistingMatch: false,
      customerLookupUrl: new URL('/resources/img/customer_lookup.svg', import.meta.url).href,
      customerSearch: null,
      searching: false,
      newCustomerToggle: false,
      existingCustomerToggle: false,
      msoPrefillToggle: false,
      msoUserIds: [],
      selectedCustomerId: null,
      searched: false,

      unlockSending: false,
      unlocked: false,
      unlockModal: 'unlockModal',
      codeInput: null,

      checkinLoading: null,
      upcomingLoading: false,

      newCustomerForm: useForm({
        firstname: null,
        lastname: null,
        email: null,
        phone: null,
        recievetext: false,
        recieveemail: false,
        send_followup: false,
        send_email_followup: false,
        text_marketing: false,
        email_marketing: false,
        text_marketing_optout: false,
        email_marketing_optout: false,
        opt_in_at: null,
        opt_in_decision: null,
        ph_id: null,
      }),
      existingCustomerForm: useForm({
        firstname: null,
        lastname: null,
        email: null,
        phone: null,
        recievetext: false,
        recieveemail: false,
        ph_id: null,
      }),
      form: useForm({
        shuttleRide: false,
        shuttle_location: null,
        appointmentType: 'd',
      }),
      settings: null,
      appointmentTypes: {
        d: 'Drop-Off',
        w: 'Waiting',
      },
      upcoming: {},
      fullName: null,
    };
  },

  computed: {
    readyForNextStep() {
      const consentReady =
        !this.requiresOptInDecision ||
        ['accept', 'decline'].includes(this.newCustomerForm.opt_in_decision);

      if (this.existingCustomerToggle) {
        return consentReady;
      }

      if (this.newCustomerToggle === true) {
        const { firstname, lastname, phone } = this.newCustomerForm;
        return !!(firstname?.trim() && lastname?.trim() && phone?.trim() && consentReady);
      }
      return false;
    },

    marketingAvailable() {
      return Number(this.masterConfig?.marketing_addon || 0) === 1;
    },

    shopDisplayName() {
      return this.masterConfig?.public_name || this.masterConfig?.shop_name || '';
    },

    optInDescription() {
      return this.settings?.opt_in_description ?? '';
    },

    privacyPolicyUrl() {
      return this.settings?.privacy_policy_url ?? '';
    },

    requiresOptInDecision() {
      return !!(this.optInDescription || this.privacyPolicyUrl);
    },

    optInDateLabel() {
      return this.newCustomerForm.opt_in_at ? formatDate(this.newCustomerForm.opt_in_at) || '' : '';
    },

    readyToSearch() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const phoneRegex = /^\d{10}$/;

      return emailRegex.test(this.customerSearch) || phoneRegex.test(this.normalizedCustomerSearch);
    },

    normalizedCustomerSearch() {
      return this.customerSearch?.replace(/-/g, '') ?? '';
    },
  },

  watch: {
    readyForNextStep(newValue, oldValue) {
      this.$emit('ready', newValue);
    },
  },

  mounted() {
    this.getSettings();

    // Check if customerID was passed (from deferred services)
    if (this.customerID !== null && this.customerID !== undefined) {
      this.loadCustomerById(this.customerID);
    } else if (this.customer !== null) {
      this.selectedCustomerId = this.customer;
      this.existingCustomerToggle = true;
      Object.keys(this.newCustomer).forEach((key) => {
        this.newCustomerForm[key] = this.newCustomer[key];
      });
    } else if (this.tireConnectCustomer?.phone_number ?? this.tireConnectCustomer?.phone) {
      this.searchByTireConnect();
    } else if (this.newCustomer.firstname !== null) {
      this.newCustomerToggle = true;
      Object.keys(this.newCustomer).forEach((key) => {
        this.newCustomerForm[key] = this.newCustomer[key];
      });
    }

    Object.keys(this.newPreferences).forEach((key) => {
      this.form[key] = this.newPreferences[key];
    });
  },

  methods: {
    formatDate,
    isValidEmail,
    APPOINTMENT_STATUS() {
      return APPOINTMENT_STATUS;
    },
    RADIO() {
      return RADIO;
    },

    async checkForExistingCustomer() {
      const { email, phone } = this.newCustomerForm;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const phoneRegex = /^\d{10}$/;

      const hasEmail = emailRegex.test(email);
      const hasPhone = phoneRegex.test(phone?.replace(/\D/g, ''));

      if (!hasEmail && !hasPhone) return;

      this.searchingForExisting = true;
      this.foundExistingMatch = false;

      const searches = [];

      if (hasEmail) {
        searches.push(
          axios.post(route('scheduler.search', { key: this.kioskKey }), { search: email }),
        );
      }

      if (hasPhone) {
        searches.push(
          axios.post(route('scheduler.search', { key: this.kioskKey }), {
            search: phone.replace(/\D/g, '').slice(-10),
          }),
        );
      }

      try {
        const results = await Promise.all(searches);
        const found = results.find((r) => r.data.data.customer !== undefined);

        if (found) {
          const customer = found.data.data.customer;

          // Capture what the user typed before we overwrite with server data
          const typedEmail = hasEmail ? this.newCustomerForm.email : null;
          const typedPhone = hasPhone ? this.newCustomerForm.phone : null;

          this.selectedCustomerId = customer.id;
          Object.keys(customer).forEach((key) => {
            this.newCustomerForm[key] = customer[key];
          });

          // Restore the field(s) the user typed — their input takes precedence
          if (typedEmail) this.newCustomerForm.email = typedEmail;
          if (typedPhone) this.newCustomerForm.phone = typedPhone;

          this.existingCustomerToggle = true;
          this.newCustomerToggle = false;
          this.foundExistingMatch = true;
          this.$emit('update', {
            customer: this.selectedCustomerId,
            newCustomer: this.newCustomerForm.data(),
          });
          if (this.searchUpcoming) {
            this.getUpcoming();
          }
        }
      } catch (e) {
        // Silently fail — don't interrupt the new customer flow
      } finally {
        this.searchingForExisting = false;
      }
    },

    loadCustomerById(customerId) {
      this.searching = true;
      this.searched = true;

      axios
        .post(
          route('scheduler.search', {
            key: this.kioskKey,
          }),
          {
            customer: customerId,
          },
        )
        .then((response) => {
          let customer = response.data.data.customer;

          if (customer === undefined) {
            this.newCustomerToggle = true;
            return;
          }

          this.selectedCustomerId = customer.id;
          Object.keys(customer).forEach((key) => {
            this.newCustomerForm[key] = customer[key];
          });
          this.existingCustomerToggle = true;
          this.$emit('update', {
            customer: this.selectedCustomerId,
            newCustomer: this.newCustomerForm.data(),
          });

          if (this.searchUpcoming) {
            this.getUpcoming();
          }
        })
        .catch((error) => {
          notyf.error('There was an error loading customer information.');
        })
        .finally(() => {
          this.searching = false;
        });
    },

    getUpcoming() {
      this.upcomingLoading = true;
      axios
        .post(
          route('scheduler.upcoming', {
            key: this.kioskKey,
          }),
          {
            id: this.selectedCustomerId,
          },
        )
        .then((response) => {
          this.upcoming = response.data.data.upcoming;
          this.fullName = response.data.data.full_name;
        })
        .finally(() => {
          this.upcomingLoading = false;
        });
    },

    updateNewCustomer(field, value) {
      const previousValue = this.newCustomerForm[field];
      this.newCustomerForm[field] = value;

      if (field === 'phone' && value !== previousValue) {
        this.newCustomerForm.ph_id = null;
        this.resetOptInState();
      }

      this.$emit('update', {
        newCustomer: this.newCustomerForm,
      });

      if (field === 'email' || field === 'phone') {
        clearTimeout(this._existingSearchTimer);
        this._existingSearchTimer = setTimeout(() => {
          this.checkForExistingCustomer();
        }, 600);
      }
    },

    updateCommunicationPreference({ field, value }) {
      this.updateNewCustomer(field, value);
    },

    updateOptInDecision(decision) {
      this.newCustomerForm.opt_in_decision = decision;

      if (decision === 'decline') {
        [
          'recievetext',
          'recieveemail',
          'send_followup',
          'send_email_followup',
          'text_marketing',
          'email_marketing',
        ].forEach((field) => {
          this.newCustomerForm[field] = false;
        });
      }

      this.$emit('update', {
        newCustomer: this.newCustomerForm,
      });
    },

    resetOptInState() {
      this.newCustomerForm.opt_in_at = null;
      this.newCustomerForm.opt_in_decision = null;
    },

    updateCustomerSearch(value) {
      const search = value ?? '';

      if (!/^[\d-]*$/.test(search)) {
        this.customerSearch = search;
        return;
      }

      const digits = search.replace(/\D/g, '').slice(0, 10);
      const parts = [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 10)].filter(Boolean);

      this.customerSearch = parts.join('-');
    },

    updatePreferences(field, value) {
      this.form[field] = value;
      this.$emit('update', {
        preferences: this.form.data(),
      });
    },

    sendUnlock() {
      this.unlockSending = true;

      axios
        .post(
          route('scheduler.unlock', {
            key: this.kioskKey,
          }),
          {
            user_id: this.selectedCustomerId,
            ph_id: this.existingCustomerForm?.ph_id,
          },
        )
        .then((response) => {
          openModal(this.unlockModal);
          notyf.success('Unlock code has been sent successfully to your phone or email.');
        })
        .catch((error) => {
          notyf.error('There was an error sending an unlock code. Please try again later.');
        })
        .finally(() => {
          this.unlockSending = false;
        });
    },

    unlockCodeSubmit() {
      this.unlockSending = true;

      axios
        .post(
          route('scheduler.unlock.code', {
            key: this.kioskKey,
          }),
          {
            code: this.codeInput,
            user_id: this.selectedCustomerId,
          },
        )
        .then((response) => {
          closeModal(this.unlockModal);
          this.codeInput = null;
          this.newCustomerForm = response.data.data.customer;
          this.unlocked = true;
          notyf.success('Customer information has been unlocked successfully.');
        })
        .catch((error) => {
          notyf.error('There was an issue with the code. Please check and try again');
        })
        .finally(() => {
          this.unlockSending = false;
        });
    },

    getSettings() {
      axios
        .post(
          route('scheduler.settings', {
            key: this.kioskKey,
          }),
        )
        .then((response) => {
          this.settings = response.data.data;
        });
    },

    checkIn(status) {
      this.checkinLoading = status.status_id;
      this.$emit('ready', false);
      this.$emit('checkin', status);
    },

    applyMsoCustomerAsNewCustomer(msoCustomer, fallbackPhone = null, fallbackEmail = null) {
      // Force "new customer" mode for this shop even when the match was found in another MSO shop.
      this.selectedCustomerId = null;
      this.existingCustomerToggle = false;
      this.newCustomerToggle = true;
      this.msoPrefillToggle = true;
      this.unlocked = false;

      this.newCustomerForm.firstname = msoCustomer.firstname || null;
      this.newCustomerForm.lastname = msoCustomer.lastname || null;
      this.newCustomerForm.email = msoCustomer.email || fallbackEmail || null;
      this.newCustomerForm.phone = msoCustomer.phone || fallbackPhone || null;
      this.newCustomerForm.recievetext = !!msoCustomer.recievetext;
      this.newCustomerForm.recieveemail = !!msoCustomer.recieveemail;
      this.newCustomerForm.send_followup = !!msoCustomer.send_followup;
      this.newCustomerForm.send_email_followup = !!msoCustomer.send_email_followup;
      this.newCustomerForm.text_marketing = !!msoCustomer.text_marketing;
      this.newCustomerForm.email_marketing = !!msoCustomer.email_marketing;
      this.newCustomerForm.text_marketing_optout = !!msoCustomer.text_marketing_optout;
      this.newCustomerForm.email_marketing_optout = !!msoCustomer.email_marketing_optout;
      this.resetOptInState();
      this.newCustomerForm.ph_id = null;

      this.$emit('update', {
        customer: null,
        msoSource: null,
        msoUserIds: this.msoUserIds,
        newCustomer: this.newCustomerForm.data(),
      });
    },

    searchCustomer() {
      this.searching = true;

      const phoneRegex = /^\d{10}$/;
      const search = phoneRegex.test(this.normalizedCustomerSearch)
        ? this.normalizedCustomerSearch
        : this.customerSearch;

      axios
        .post(
          route('scheduler.search', {
            key: this.kioskKey,
          }),
          {
            search,
          },
        )
        .then((response) => {
          let customer = response.data.data.customer;
          let msoCustomer = response.data.data?.mso_customer;
          this.msoUserIds = response.data.data?.mso_user_ids ?? [];
          this.searched = true;
          if (!customer) {
            this.newCustomerToggle = true;
            this.msoPrefillToggle = false;

            if (msoCustomer) {
              this.applyMsoCustomerAsNewCustomer(msoCustomer);
              return;
            }

            this.resetOptInState();

            //If Email then add to new customer
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (emailRegex.test(this.customerSearch)) {
              this.searchedFields.add('email');
              this.newCustomerForm.email = this.customerSearch;
            }

            if (phoneRegex.test(this.normalizedCustomerSearch)) {
              this.searchedFields.add('phone');
              this.newCustomerForm.phone = this.normalizedCustomerSearch;
            }
            return;
          }
          this.selectedCustomerId = customer.id;
          this.msoPrefillToggle = false;
          this.msoUserIds = [];
          Object.keys(customer).forEach((key) => {
            this.newCustomerForm[key] = customer[key];
          });
          this.existingCustomerToggle = true;
          this.$emit('update', {
            customer: this.selectedCustomerId,
            msoSource: null,
            msoUserIds: [],
            newCustomer: this.newCustomerForm.data(),
          });
          if (this.searchUpcoming) {
            this.getUpcoming();
          }
        })
        .catch((error) => {})
        .finally(() => {
          this.searching = false;
        });
    },

    searchByTireConnect() {
      const tc = this.tireConnectCustomer;
      // Flat format uses phone_number; quote-wrapped format uses phone.
      const rawPhone = tc?.phone_number ?? tc?.phone;
      if (!rawPhone) return;

      // Clean the phone number to 10 digits
      const cleanPhone = rawPhone.replace(/\D/g, '').slice(-10);
      this.customerSearch = cleanPhone;
      this.searching = true;

      const prefillFromTireConnect = () => {
        this.newCustomerToggle = true;
        this.resetOptInState();
        this.newCustomerForm.phone = cleanPhone;
        // Flat format: first_name + last_name fields
        // Quote-wrapped format: a single name field to split
        if (tc.first_name || tc.last_name) {
          this.newCustomerForm.firstname = tc.first_name || null;
          this.newCustomerForm.lastname = tc.last_name || null;
        } else if (tc.name) {
          const nameParts = tc.name.trim().split(/\s+/);
          this.newCustomerForm.firstname = nameParts[0] || null;
          this.newCustomerForm.lastname = nameParts.slice(1).join(' ') || null;
        }
        if (tc.email) {
          this.newCustomerForm.email = tc.email;
        }
      };

      axios
        .post(
          route('scheduler.search', {
            key: this.kioskKey,
          }),
          {
            search: cleanPhone,
          },
        )
        .then((response) => {
          let customer = response.data.data.customer;
          let msoCustomer = response.data.data?.mso_customer;
          this.msoUserIds = response.data.data?.mso_user_ids ?? [];
          this.searched = true;

          if (customer) {
            // Existing customer found — use their stored data
            this.selectedCustomerId = customer.id;
            this.msoPrefillToggle = false;
            this.msoUserIds = [];
            Object.keys(customer).forEach((key) => {
              this.newCustomerForm[key] = customer[key];
            });
            this.existingCustomerToggle = true;
            this.$emit('update', {
              customer: this.selectedCustomerId,
              msoSource: null,
              msoUserIds: [],
              newCustomer: this.newCustomerForm.data(),
            });
            if (this.searchUpcoming) {
              this.getUpcoming();
            }
          } else if (msoCustomer) {
            this.applyMsoCustomerAsNewCustomer(msoCustomer, cleanPhone, tc.email || null);
          } else {
            // No existing customer — fall back to TireConnect data
            prefillFromTireConnect();
            this.newCustomerToggle = true;
            this.msoPrefillToggle = false;
            this.msoUserIds = [];
            this.newCustomerForm.phone = cleanPhone;
            if (tc.name) {
              const nameParts = tc.name.trim().split(/\s+/);
              this.newCustomerForm.firstname = nameParts[0] || null;
              this.newCustomerForm.lastname = nameParts.slice(1).join(' ') || null;
            }
            if (tc.email) {
              this.newCustomerForm.email = tc.email;
            }
          }
        })
        .catch(() => {
          // On error, still pre-fill from TireConnect data
          prefillFromTireConnect();
        })
        .finally(() => {
          this.searching = false;
        });
    },
  },
};
</script>

<template>
  <Card v-if="searchUpcoming && searched && !customerID" class="mt-3">
    <template v-if="upcomingLoading">
      <div class="text-center mb-3">Please wait while we check for upcoming appointments...</div>
      <Loading />
    </template>

    <template v-else-if="upcomingLoading === false && Object.keys(upcoming).length !== 0">
      <div class="text-center mb-4">
        <h5>Welcome back, {{ fullName }}. Please confirm a few details</h5>
      </div>

      <h6 class="card-title font-weight-bold text-primary text-center mb-5">
        Are you checking in for
        {{
          Object.keys(upcoming).length === 1
            ? 'the following visit?'
            : 'one of the following visits?'
        }}
      </h6>
      <table class="table table-borderless mb-3 table-striped">
        <tbody>
          <template v-for="status in upcoming">
            <tr>
              <td>
                {{ status?.vehicle?.full_vehicle }} |
                {{
                  status.status == APPOINTMENT_STATUS()
                    ? 'Appointment: ' + formatDate(status.scheduled_datetime)
                    : 'Current Active'
                }}
              </td>
              <td class="text-end">
                <button
                  :disabled="checkinLoading == status.status_id"
                  class="btn btn-success"
                  @click="checkIn(status)"
                >
                  <Spinner v-if="checkinLoading == status.status_id" /> Check-in
                </button>
              </td>
            </tr>
          </template>
        </tbody>
      </table>

      <Alert icon="fa fa-info-circle">
        Please select or click "next" to add a different vehicle
      </Alert>
    </template>
  </Card>

  <Card id="customer_info" class="mt-3">
    <template v-if="newCustomerToggle === false && existingCustomerToggle === false">
      <div class="text-center mt-5">
        <img :src="customerLookupUrl" alt="Search icon" class="icon" />
      </div>
      <!-- Subtitle -->
      <div class="subtitle text-center">Let's find you in our system</div>
      <div class="row">
        <label class="col-form-label text-center">Enter your phone number or email</label>
      </div>
      <div class="subtitle text-center">
        (For returning customers, please enter the primary phone number or email on the account)
      </div>
      <!-- Input -->
      <div class="form-group w-100 mt-4 text-center">
        <TextInput
          :disabled="searching === true"
          placeholder="xxx-xxx-xxxx (or) name@email.com"
          :model-value="customerSearch"
          @keyup:model-value="updateCustomerSearch"
          @update:model-value="updateCustomerSearch"
          @blur:model-value="updateCustomerSearch"
        />
      </div>

      <div v-if="searching" class="mt-3">
        <Loading />
      </div>

      <!-- Proceed Button -->
      <div v-if="newCustomerToggle === false && searching === false" class="form-group w-100 mt-3">
        <button
          :disabled="readyToSearch === false || searching === true"
          class="btn btn-primary btn-block"
          @click="searchCustomer"
        >
          Search
        </button>
      </div>
    </template>

    <template v-if="newCustomerToggle || existingCustomerToggle">
      <div class="mt-1">
        <Alert v-if="searched === true && newCustomerToggle === true && msoPrefillToggle === false">
          <strong>Hmm... we couldn't find ya. Let's add you in!</strong>
        </Alert>

        <div class="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4>Customer Details</h4>
          </div>
          <button
            v-if="selectedCustomerId !== null"
            :disabled="unlockSending === true || unlocked === true"
            class="btn btn-primary"
            @click="sendUnlock()"
          >
            <Spinner v-if="unlockSending" />
            <Lock v-else /> Unlock to Make Changes
          </button>
        </div>

        <div class="row">
          <div class="col-12 col-sm-6">
            <div class="form-group mb-3">
              <Label :required="true">First Name</Label>
              <TextInput
                :disabled="(existingCustomerToggle || msoPrefillToggle) && unlocked === false"
                placeholder="Enter First Name"
                autocomplete="given-name"
                :model-value="newCustomerForm.firstname"
                @update:model-value="(newValue) => updateNewCustomer('firstname', newValue)"
                @input:model-value="(newValue) => updateNewCustomer('firstname', newValue)"
              />
            </div>
          </div>

          <div class="col-12 col-sm-6">
            <div class="form-group mb-3">
              <Label :required="true">Last Name</Label>
              <TextInput
                :disabled="(existingCustomerToggle || msoPrefillToggle) && unlocked === false"
                placeholder="Enter Last Name"
                autocomplete="family-name"
                :model-value="newCustomerForm.lastname"
                @update:model-value="(newValue) => updateNewCustomer('lastname', newValue)"
                @input:model-value="(newValue) => updateNewCustomer('lastname', newValue)"
              />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12 col-sm-6">
            <div class="form-group mb-3">
              <Label :required="false">Email</Label>
              <TextInput
                :disabled="
                  (existingCustomerToggle || msoPrefillToggle) &&
                  unlocked === false &&
                  !searchedFields.has('email')
                "
                type="email"
                placeholder="Enter Email"
                autocomplete="email"
                :model-value="newCustomerForm.email"
                @update:model-value="(newValue) => updateNewCustomer('email', newValue)"
                @input:model-value="(newValue) => updateNewCustomer('email', newValue)"
              />
            </div>
          </div>
          <div class="col-12 col-sm-6">
            <div class="form-group mb-3">
              <Label :required="true">Phone Number</Label>
              <TextInput
                v-if="
                  existingCustomerToggle &&
                  unlocked === false &&
                  newCustomerForm.phone?.includes('x')
                "
                :disabled="true"
                placeholder="Enter Phone Number"
                :model-value="newCustomerForm.phone"
              />
              <PhoneNumber
                v-else
                :disabled="
                  (existingCustomerToggle || msoPrefillToggle) &&
                  newCustomerForm.phone !== null &&
                  unlocked === false &&
                  !searchedFields.has('phone')
                "
                placeholder="Enter Phone Number"
                autocomplete="tel-national"
                :model-value="newCustomerForm.phone"
                @update:model-value="(newValue) => updateNewCustomer('phone', newValue)"
              />
            </div>
          </div>
        </div>

        <div
          v-if="searchingForExisting"
          class="alert alert-info d-flex align-items-center gap-2 mb-3"
        >
          <Spinner />
          <span>Checking if you're already in our system...</span>
        </div>

        <hr />

        <h4>Appointment Preferences</h4>

        <table class="table">
          <tbody>
            <tr
              v-if="
                (settings?.slot_settings?.force_dropoff == false ||
                  settings?.slot_settings?.force_dropoff === null) &&
                settings?.slot_settings?.show_wait_dropoff == true &&
                waiterAvailable
              "
            >
              <TableOption
                title="Appointment Type"
                note="Will you be waiting or dropping off your vehicle?"
                :type="RADIO()"
                :options="appointmentTypes"
                :model-value="form.appointmentType"
                @update:model-value="(newValue) => updatePreferences('appointmentType', newValue)"
              />
            </tr>

            <tr v-if="settings?.slot_settings?.shuttle_service == true">
              <TableOption
                title="Shuttle Ride"
                note="Will you need a shuttle ride back to home/work?"
                :model-value="form.shuttleRide == true ? 1 : 0"
                @update:model-value="(newValue) => updatePreferences('shuttleRide', newValue)"
              >
                <AddressAutoComplete
                  v-if="form.shuttleRide == true"
                  class="mt-2"
                  placeholder="Enter Shuttle Address/Location"
                  :api-key="autocompleteKey"
                  @update:model-value="(newValue) => (form.shuttle_location = newValue)"
                />
              </TableOption>
            </tr>
          </tbody>
        </table>

        <CommunicationPreferences
          :preferences="newCustomerForm"
          :shop-name="shopDisplayName"
          :marketing-available="marketingAvailable"
          :email-available="isValidEmail(newCustomerForm.email)"
          :opt-in-description="optInDescription"
          :privacy-policy-url="privacyPolicyUrl"
          :opt-in-decision="newCustomerForm.opt_in_decision"
          :opt-in-date="optInDateLabel"
          @update:preference="updateCommunicationPreference"
          @update:opt-in-decision="updateOptInDecision"
        />
      </div>
    </template>
  </Card>

  <SlideOut :id="unlockModal" title-icon-class="fa fa-lock" title="Unlock Customer Information">
    <Alert>
      A code has been sent to your phone or email. Please enter the code below to unlock the
      customer information.
    </Alert>

    <Label :required="true">Enter code to Unlock Information</Label>
    <TextInput
      placeholder="Code"
      :model-value="codeInput"
      @update:model-value="(newValue) => (codeInput = newValue)"
    />

    <template #footer>
      <button :disabled="unlockSending" class="btn btn-primary" @click="unlockCodeSubmit()">
        <Spinner v-if="unlockSending" /> Confirm Code
      </button>
    </template>
  </SlideOut>
</template>

<style scoped>
.card#customer_info {
  min-height: 700px;
}
</style>
